/** @file Edit-mesh pointer behaviour for the viewport tool table: press gestures, idle wire, overlay layers. */
import { HANDLE_PX } from '@pose2d/editor-geometry/handlePx';
import { pickPoint } from '@pose2d/editor-geometry/pickPoint';
import { OVERLAY_STYLE } from '@pose2d/overlay/overlayStyle';
import type { OverlayOptions } from '@pose2d/overlay/types';
import type { ToolContext, ToolPointer, ViewportPointer } from '../../panels/viewport/viewportTypes';
import { selectionStore } from '../../stores/selectionStore';
import { editMeshPress } from './editMeshPress';
import { editMeshStore } from './editMeshStore';
import { meshActionGesture } from './meshActionGesture';
import { meshGizmo } from './meshGizmo';
import { meshScene } from './meshScene';
import { vertexSelection } from './vertexSelection';

/** E1's ToolPointer plus the two hooks this area asks E1 to call (R-E5-10): overlay layers and hover moves. */
export interface MeshToolPointer extends ToolPointer {
  /** Overlay options merged over the viewport toggles each frame. */
  overlay(ctx: ToolContext): OverlayOptions;
  /** A pointer move without a gesture; null when the pointer leaves the canvas. */
  hover?(ctx: ToolContext, p: ViewportPointer | null): void;
}

/** Modify/create presses become editMeshPress gestures; a delete-mode press on a vertex deletes it on release. */
export const editMeshPointer: MeshToolPointer = {
  press(ctx, p) {
    const s = p.button === 0 ? meshScene(ctx) : null;
    if (s === null) {
      if (p.button === 0) ctx.notify({ kind: 'info', key: 'tools.editMesh.noMesh' });
      return null;
    }
    const hit = pickPoint({ coords: s.points, stride: 2, offset: 0, count: s.points.length / 2 }, ctx.camera, p.sx, p.sy, HANDLE_PX.point);
    if (editMeshStore.getState().mode !== 'delete') return editMeshPress(s, p, hit);
    return hit < 0 ? null : meshActionGesture(s, 'deleteVertices', [hit]);
  },
  idle(ctx) {
    const s = meshScene(ctx);
    if (s === null) return null;
    const picked = new Set(vertexSelection.of(selectionStore.getState(), s.target.attachment, s.points.length / 2));
    return meshGizmo(ctx.camera, s.points, s.target.mesh.triangles, (v) => (picked.has(v) ? OVERLAY_STYLE.selected : OVERLAY_STYLE.vertex));
  },
  overlay: () => (editMeshStore.getState().showIds ? { vertexIds: true } : {}),
};
